<div class="video-preview">
  @let deviceError = deviceService.videoDeviceError$ | async;
  @let camStream = localStream.camStream$ | async;
  @if (!deviceError) {
    @switch (permissions.cameraAccess()) {
      @case (null) {
        Loading...
      }
      @case ('prompt') {
        <andor-media-access-prompt
          [icon]="faCameraMovie"
          [text]="
            'This app requires access to your camera to enable video features and enhance your experience. Camera access is used solely for functionality and is never recorded or shared. Please grant permission to unlock all available features.'
              | translate
          "
          (onRequestAccess)="requestCam()"></andor-media-access-prompt>
      }
      @case ('denied') {
        <andor-media-access-denied
          [text]="
            'Camera access is currently blocked. To use video features, please adjust your browser settings to allow camera access. This will enable all video functionalities of the app.'
              | translate
          "
          (onShowTutorial)="permissions.openPermissionTutorial()"></andor-media-access-denied>
      }
      @default {
        <div class="default">
          @if (deviceService.videoDeviceOptions$ | async; as options) {
            <andor-select-field
              [options]="options"
              [selectedOption]="deviceService.selectedVideoDevice$ | async"
              (selected)="deviceService.selectVideoDevice($event.id)"
              [preferredDirection]="direction()"
              [forceDisabled]="isChromiumBrowser"
              [toolTipMsg]="
                isChromiumBrowser
                  ? ('Camera selection is managed through your browser settings.' | translate)
                  : ''
              "
              [title]="'Video Device' | translate"></andor-select-field>
          }

          @if (allowCameraTest()) {
            @if (localStream.camError(); as camError) {
              @if (camError.kind === 'permission') {
                <!-- Retrying a denied permission never re-prompts — reuse
                     the existing blocked-access UI with its tutorial. -->
                <andor-media-access-denied
                  [text]="
                    'Camera access is currently blocked. To use video features, please adjust your browser settings to allow camera access. This will enable all video functionalities of the app.'
                      | translate
                  "
                  (onShowTutorial)="permissions.openPermissionTutorial()"></andor-media-access-denied>
              } @else {
                <andor-notice variant="warning">
                  <div class="cam-error-content">
                    <strong>{{ camErrorMessage(camError) | translate }}</strong>
                    <div class="cam-error-causes">
                      <span class="cam-error-causes-label">{{ 'Possible causes' | translate }}</span>
                      <span>{{ 'No camera found: connect a camera, then retry.' | translate }}</span>
                      <span>{{
                        'Camera in use: close the other application that is using it, then retry.'
                          | translate
                      }}</span>
                      <span>{{
                        'Access blocked: allow camera access in your browser settings.' | translate
                      }}</span>
                    </div>
                    <andor-button color="warning" density="compact" (click)="startCam()">
                      {{ 'Retry' | translate }}
                    </andor-button>
                  </div>
                </andor-notice>
              }
            } @else {
              <div class="preview-area">
                @if (camStream && showCamPreview()) {
                  <div class="video-container">
                    @if (camStream | nativeVideo) {
                      <canvas class="mirror" [andorVideoMount]="camStream"></canvas>
                    } @else {
                      <video
                        [srcObject]="camStream"
                        class="mirror"
                        preload="none"
                        autoplay
                        playsinline
                        disablePictureInPicture
                        [disableRemotePlayback]="true"
                        controlsList="noplaybackrate nodownload"
                        muted></video>
                    }
                    @if (!isPreviewDialog()) {
                      <andor-icon-btn (click)="stopCam()">
                        <fa-icon [icon]="faClose"></fa-icon>
                      </andor-icon-btn>
                    }
                  </div>
                } @else if (showCamPreview() || autoStartCamera()) {
                  <div class="loading">
                    <img class="bounce" src="../../../../../assets/icon_dark.svg" />
                    <span>{{ 'Loading...' | translate }}</span>
                  </div>
                } @else {
                  <andor-button (click)="startCam()">{{ 'Test Camera' | translate }}</andor-button>
                }
              </div>
            }
          }

          @if (!blurAvailable) {
            <div class="background-blur-warning">
              {{ 'Background blur is not available in the app yet.' | translate }}
            </div>
          } @else {
            @if (!isBackgroundBlurSupported && !camStream) {
              <div class="background-blur-warning">
                {{
                  'Background blur may not work correctly with your current browser and operating system combination. If you experience issues, try using Firefox instead.'
                    | translate
                }}
              </div>
            }
            <andor-mat-toggle-wrapper
              label="{{ 'Background Blur' | translate }}"
              tooltipText="{{
                'Blur your background to keep the focus on you and reduce visual distractions during calls.'
                  | translate
              }}">
              <mat-slide-toggle
                (toggleChange)="updateBackgroundBlurEnabled()"
                [checked]="backgroundBlurEnabled()"></mat-slide-toggle>
            </andor-mat-toggle-wrapper>

            @if (backgroundBlurEnabled()) {
              <andor-mat-slider-wrapper
                label="{{ 'Blur Intensity' | translate }}"
                tooltipText="{{
                  'Adjust how strongly your background is blurred. A higher value creates a more pronounced effect.'
                    | translate
                }}">
                <mat-slider [max]="30" [min]="1" [step]="1" discrete>
                  <input
                    matSliderThumb
                    (valueChange)="updateBackgroundBlurAmount($event)"
                    [ngModel]="backgroundBlurAmount()" />
                </mat-slider>
              </andor-mat-slider-wrapper>
            }
          }

          @if (!isPreviewDialog()) {
            <andor-mat-toggle-wrapper
              label="{{ 'Always show Camera Preview' | translate }}"
              tooltipText="{{
                'Show a preview dialog before enabling the camera. When disabled, the camera starts immediately with the current settings.'
                  | translate
              }}">
              <mat-slide-toggle
                (toggleChange)="updateShowCameraPreview()"
                [checked]="showCameraPreview()"></mat-slide-toggle>
            </andor-mat-toggle-wrapper>

            @if (showTestComponents) {
              <hr />

              <andor-mat-toggle-wrapper
                label="{{ 'Override Camera Settings' | translate }}"
                tooltipText="{{ 'Override the default camera capture settings.' | translate }}">
                <mat-slide-toggle
                  (toggleChange)="toggleCameraOverride()"
                  [checked]="overrideCameraSettings()"></mat-slide-toggle>
              </andor-mat-toggle-wrapper>

              @if (overrideCameraSettings()) {
                <div class="override-settings">
                  <div class="tabs-wrapper">
                    <div class="tabs-label">
                      {{ 'Codec' | translate }}
                      <fa-icon
                        class="info"
                        [icon]="faInfoCircle"
                        [andorTooltip]="
                          'Video codec used for encoding. Only codecs supported by your browser are shown.'
                            | translate
                        "
                        [tooltipTitle]="'Codec' | translate"></fa-icon>
                    </div>
                    <andor-tabs
                      [tabs]="codecTabs"
                      [active]="getCodecTab(cameraSettings())"
                      (activeChange)="$event && onCodecChange($event, 'camera')"></andor-tabs>
                  </div>

                  <div class="tabs-wrapper">
                    <div class="tabs-label">
                      {{ 'Resolution' | translate }}
                      <fa-icon
                        class="info"
                        [icon]="faInfoCircle"
                        [andorTooltip]="
                          'Maximum video resolution for the camera capture.' | translate
                        "></fa-icon>
                    </div>
                    <andor-tabs
                      [tabs]="resolutionTabs"
                      [active]="getResolutionTab(cameraSettings())"
                      (activeChange)="$event && onResolutionChange($event, 'camera')"></andor-tabs>
                  </div>

                  <div class="tabs-wrapper">
                    <div class="tabs-label">
                      {{ 'Degradation Preference' | translate }}
                      <fa-icon
                        class="info"
                        [icon]="faInfoCircle"
                        [andorTooltip]="
                          'Controls how the encoder adapts under network constraints. Balanced trades off both, FPS keeps framerate steady, Resolution keeps image sharpness.'
                            | translate
                        "
                        [tooltipTitle]="'Degradation Preference' | translate"></fa-icon>
                    </div>
                    <andor-tabs
                      [tabs]="degradationTabs"
                      [active]="getDegradationTab(cameraSettings())"
                      (activeChange)="$event && onDegradationChange($event, 'camera')"></andor-tabs>
                  </div>

                  <div class="stepper-row">
                    <div class="tabs-label">
                      {{ 'Max Framerate' | translate }}
                      <fa-icon
                        class="info"
                        [icon]="faInfoCircle"
                        [andorTooltip]="
                          'Upper limit for frames per second sent to other participants.'
                            | translate
                        "></fa-icon>
                    </div>
                    <andor-stepper
                      [steps]="validFramerates"
                      [value]="cameraSettings().maxFramerate"
                      (valueChange)="updateCameraSetting({ maxFramerate: $any($event) })"
                      unit="fps"></andor-stepper>
                  </div>

                  <div class="stepper-row">
                    <div class="tabs-label">
                      {{ 'Max Bitrate' | translate }}
                      <fa-icon
                        class="info"
                        [icon]="faInfoCircle"
                        [andorTooltip]="
                          'Upper limit for the video encoding bitrate in kilobits per second.'
                            | translate
                        "></fa-icon>
                    </div>
                    <andor-stepper
                      [steps]="validBitrates"
                      [value]="cameraSettings().maxBitrate"
                      (valueChange)="updateCameraSetting({ maxBitrate: $any($event) })"
                      unit="kbps"></andor-stepper>
                  </div>
                </div>
              }

              @if (screenShareSupported) {
                <andor-mat-toggle-wrapper
                  label="{{ 'Override Screen Share Settings' | translate }}"
                  tooltipText="{{
                    'Override the default screen share capture settings.' | translate
                  }}">
                  <mat-slide-toggle
                    (toggleChange)="toggleScreenOverride()"
                    [checked]="overrideScreenSettings()"></mat-slide-toggle>
                </andor-mat-toggle-wrapper>

                @if (overrideScreenSettings()) {
                  <div class="override-settings">
                    <div class="tabs-wrapper">
                      <div class="tabs-label">
                        {{ 'Codec' | translate }}
                        <fa-icon
                          class="info"
                          [icon]="faInfoCircle"
                          [andorTooltip]="
                            'Video codec used for encoding. Only codecs supported by your browser are shown.'
                              | translate
                          "
                          [tooltipTitle]="'Codec' | translate"></fa-icon>
                      </div>
                      <andor-tabs
                        [tabs]="codecTabs"
                        [active]="getCodecTab(screenSettings())"
                        (activeChange)="$event && onCodecChange($event, 'screen')"></andor-tabs>
                    </div>

                    <div class="tabs-wrapper">
                      <div class="tabs-label">
                        {{ 'Resolution' | translate }}
                        <fa-icon
                          class="info"
                          [icon]="faInfoCircle"
                          [andorTooltip]="
                            'Maximum video resolution for the screen share capture.' | translate
                          "></fa-icon>
                      </div>
                      <andor-tabs
                        [tabs]="resolutionTabs"
                        [active]="getResolutionTab(screenSettings())"
                        (activeChange)="$event && onResolutionChange($event, 'screen')"></andor-tabs>
                    </div>

                    <div class="tabs-wrapper">
                      <div class="tabs-label">
                        {{ 'Degradation Preference' | translate }}
                        <fa-icon
                          class="info"
                          [icon]="faInfoCircle"
                          [andorTooltip]="
                            'Controls how the encoder adapts under network constraints. Balanced trades off both, FPS keeps framerate steady, Resolution keeps image sharpness.'
                              | translate
                          "
                          [tooltipTitle]="'Degradation Preference' | translate"></fa-icon>
                      </div>
                      <andor-tabs
                        [tabs]="degradationTabs"
                        [active]="getDegradationTab(screenSettings())"
                        (activeChange)="$event && onDegradationChange($event, 'screen')"></andor-tabs>
                    </div>

                    <div class="stepper-row">
                      <div class="tabs-label">
                        {{ 'Max Framerate' | translate }}
                        <fa-icon
                          class="info"
                          [icon]="faInfoCircle"
                          [andorTooltip]="
                            'Upper limit for frames per second sent to other participants.'
                              | translate
                          "></fa-icon>
                      </div>
                      <andor-stepper
                        [steps]="validFramerates"
                        [value]="screenSettings().maxFramerate"
                        (valueChange)="updateScreenSetting({ maxFramerate: $any($event) })"
                        unit="fps"></andor-stepper>
                    </div>

                    <div class="stepper-row">
                      <div class="tabs-label">
                        {{ 'Max Bitrate' | translate }}
                        <fa-icon
                          class="info"
                          [icon]="faInfoCircle"
                          [andorTooltip]="
                            'Upper limit for the video encoding bitrate in kilobits per second.'
                              | translate
                          "></fa-icon>
                      </div>
                      <andor-stepper
                        [steps]="validBitrates"
                        [value]="screenSettings().maxBitrate"
                        (valueChange)="updateScreenSetting({ maxBitrate: $any($event) })"
                        unit="kbps"></andor-stepper>
                    </div>
                  </div>
                }
              }
            }
          }
        </div>
      }
    }
  } @else {
    <div class="device-error">{{ deviceError }}</div>
  }
</div>
